import { formatRate } from './counters-format'
import { describeQueue } from './decay-format'

/**
 * THE WEEKLY OWNER DIGEST — wording (Build 9, commit B).
 *
 * WHY THE DIGEST EXISTS. The owner never sees what his assistant does for him.
 * The counters exist and nothing reports them, and an invisible product is the
 * one that gets cancelled the first tight month. One Monday text — what it did
 * this week — is the owner-stays-in-control promise made literal: visibility IS
 * the control.
 *
 * DEPENDENCY-FREE, like counters-format.ts and decay-format.ts, and for the same
 * load-bearing reason: anything both lanes may need lives in a file with no
 * runtime imports. Its only imports are those two modules, which hold the same
 * rule. That is also deliberate on its own account: the answer rate is floored
 * by THE SAME formatRate the dashboard tile uses, and the confirm line is worded
 * by THE SAME describeQueue the dashboard heading uses — so the text on his
 * phone and the screen he lands on cannot say different things about one number.
 *
 * WHAT THE OWNER MUST NEVER BE TOLD, as a list — every branch below exists to
 * stop one of these:
 *   - "0%" when nobody asked anything (0/0 is no traffic, not a failure);
 *   - "100%" when one question went unanswered (floor, not round);
 *   - "0 leads" for days before leads were being counted at all;
 *   - "nothing needs confirming" when the chase has merely gone quiet;
 *   - nothing at all, on a dead week — silence reads as broken.
 *
 * GSM-7 ONLY, and enforced here rather than hoped for. One character outside
 * the GSM-7 set (a curly apostrophe in a brand name, an em dash in a template)
 * flips the WHOLE message to UCS-2: 67 characters a segment instead of 153, so
 * a 3-segment text silently becomes 7. Templates here use ASCII only, and every
 * value that comes from configuration is passed through toGsm7 before it is
 * placed. tests/digest-format.test.ts asserts every output character is GSM-7
 * and that the worst case fits DIGEST_MAX_SEGMENTS.
 *
 * COUNTS ONLY, NEVER CONTENT — the decay notice's rule, kept. A text sits on a
 * lock screen and survives on a handset he might lend or lose; no question, no
 * fact and no customer detail is ever placed in it. Numbers and a link.
 */

/* ---------------------------------------------------------------------------
 * The input. Every field is a number the engine derived from the event log at
 * send time (workers/digest.ts), plus configuration. Nothing here is stored.
 * ------------------------------------------------------------------------- */

export interface DigestInput {
  /** The product sending this — 'Foredesk', 'Kelvo'. Configuration. */
  productName: string
  /** The owner's business, as the pod names it. Configuration. */
  brandName: string
  /** Exclusive end of the 7-day window, unix seconds. Labels the week. */
  windowEndSeconds: number

  /** lead_captured events in the window. */
  leads: number
  /**
   * Set when the window began BEFORE lead_captured existed: leads are only
   * counted from this instant (unix seconds), and the line says so. Null on
   * every week after the first. Derived by the caller, never stored.
   */
  leadsCountedFrom: number | null

  /** question_asked / question_answered in the window. */
  asked: number
  answered: number

  /** Gaps — questions his assistant passed to him. Opened, answered and
   *  dismissed in the window (an answer may close a gap opened earlier). */
  gapsOpened: number
  gapsAnswered: number
  gapsDismissed: number

  /** Facts due for re-confirmation right now. */
  decayDue: number
  /**
   * False once the chase has gone quiet (MAX_UNANSWERED_NOTICES unanswered
   * weeks in workers/decay.ts). The digest still sends; only this line drops.
   */
  decayChase: boolean

  /** Where "see it all" points. Configuration. */
  dashboardUrl: string

  /**
   * A STAFF POD'S WEEK (Kelvo, Build 10e). Present only for a pod whose
   * assistant answers the firm's own staff: the text then speaks of staff
   * questions and what was not written down, carries no leads line (a staff
   * pod takes no enquiries), and adds the renewals line. Absent: Foredesk's
   * text, byte for byte as it was.
   */
  staff?: StaffWeek
}

/** The extra numbers a staff pod's week carries, derived at send time. */
export interface StaffWeek {
  /** Of the gaps opened this week, how many were about safety (workers/
   *  safety-topic.ts). Null when the pod has no safety words: no split. */
  safetyGaps: number | null
  /** The renewals list as it stands on Monday morning, by the dashboard's
   *  own bands. Null when the pod keeps no renewals list. */
  renewals: { overdue: number; soon: number } | null
}

/* ---------------------------------------------------------------------------
 * GSM-7.
 * ------------------------------------------------------------------------- */

/** The GSM 03.38 basic character set (one septet each). '\n' and '\r' included. */
const GSM7_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
  '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà'

/** The extension table: valid GSM-7, but each costs TWO septets (escape + char). */
const GSM7_EXTENSION = '^{}\\[~]|€'

const BASIC = new Set(GSM7_BASIC)
const EXTENSION = new Set(GSM7_EXTENSION)

/** Is every character of this text encodable in GSM-7 (basic or extension)? */
export function isGsm7(text: string): boolean {
  for (const ch of text) {
    if (!BASIC.has(ch) && !EXTENSION.has(ch)) return false
  }
  return true
}

/**
 * Common look-alikes, mapped to their GSM-7 equivalent rather than dropped.
 * These are exactly the characters a brand name typed on a phone or pasted from
 * a website arrives with — the curly apostrophe in "O’Brien" above all.
 */
const LOOKALIKES: Record<string, string> = {
  '‘': "'", // ‘
  '’': "'", // ’
  '‚': "'", // ‚
  '‛': "'", // ‛
  '′': "'", // ′
  '“': '"', // “
  '”': '"', // ”
  '„': '"', // „
  '″': '"', // ″
  '‐': '-', // ‐
  '‑': '-', // ‑
  '‒': '-', // ‒
  '–': '-', // –
  '—': '-', // —
  '―': '-', // ―
  '−': '-', // −
  '…': '...', // …
  ' ': ' ', // no-break space
  ' ': ' ',
  ' ': ' ',
  ' ': ' ',
  ' ': ' ',
  '\t': ' ',
  // Letters NFD cannot decompose (they are letters, not accented ones), so the
  // accent-strip below would otherwise drop them and mangle the name.
  '\u0141': 'L', // Ł
  '\u0142': 'l', // ł
  '\u0110': 'D', // Đ
  '\u0111': 'd', // đ
  '\u0131': 'i', // ı
}

/**
 * Make configuration text safe to place in a GSM-7 message.
 *
 * Per character: keep it if GSM-7 already has it; map a known look-alike; else
 * strip its accent if the bare letter is GSM-7 ("Café" stays "Café" — é is in
 * the set — but "Łódź" becomes "Lodz"); else DROP it. Dropping is the right
 * failure for a brand name: one missing ornament costs nothing, one emoji left
 * in would cost the owner four extra segments every week, for ever, silently.
 *
 * Whitespace runs are collapsed, so a dropped character never leaves a gap.
 */
export function toGsm7(text: string): string {
  let out = ''
  for (const ch of text) {
    if (BASIC.has(ch) || EXTENSION.has(ch)) {
      out += ch
      continue
    }
    const mapped = LOOKALIKES[ch]
    if (mapped !== undefined) {
      out += mapped
      continue
    }
    const base = ch.normalize('NFD').replace(/[̀-ͯ]/g, '')
    if (base && [...base].every((c) => BASIC.has(c))) out += base
  }
  return out.replace(/[ ]{2,}/g, ' ').trim()
}

/**
 * How many SMS segments this text costs, assuming it is GSM-7 (the caller
 * guarantees that; isGsm7 checks it). Extension characters count double.
 * One segment holds 160 septets; once split, each part holds 153 (the rest is
 * the concatenation header).
 */
export function gsm7Segments(text: string): number {
  let septets = 0
  for (const ch of text) septets += EXTENSION.has(ch) ? 2 : 1
  if (septets <= 160) return 1
  return Math.ceil(septets / 153)
}

/** The budget the worst-case digest is held to in tests. Three segments is the
 *  ceiling the spec priced (~2–4p a week per owner); a digest that needs more
 *  is a digest saying too much to be read on a lock screen. */
export const DIGEST_MAX_SEGMENTS = 3

/* ---------------------------------------------------------------------------
 * The opt-out.
 * ------------------------------------------------------------------------- */

/**
 * The reply that turns the digest off. ONE CONSTANT, imported by the inbound
 * handler (commit E) as well as printed here, so the instruction in the text
 * and the word the engine listens for cannot drift apart.
 *
 * DELIBERATELY NOT "STOP". STOP is a carrier- and Twilio-level opt-out keyword:
 * depending on how the sending number is configured, a STOP reply can block
 * EVERY future message from that number to the owner — his JobBriefs and
 * missed-call alerts included. Telling him to send it to silence a weekly
 * report would risk switching off the leads he is paying for. A word of our
 * own turns off exactly what it says and nothing else.
 */
export const DIGEST_OFF_KEYWORD = 'DIGEST OFF'
export const DIGEST_ON_KEYWORD = 'DIGEST ON'

/* ---------------------------------------------------------------------------
 * Wording.
 * ------------------------------------------------------------------------- */

/** Brand names are configuration and could be long. Capped so one long name
 *  cannot push the message over budget; cut with ASCII dots, never '…'. */
const MAX_BRAND_CHARS = 40
const MAX_PRODUCT_CHARS = 20

function capped(text: string, max: number): string {
  const t = toGsm7(text)
  return t.length > max ? `${t.slice(0, max - 3).trimEnd()}...` : t
}

/** A count from the engine, made safe: whole, finite, non-negative. A garbled
 *  value becomes 0 rather than "NaN" or "-1" on the owner's phone. */
function count(n: number): number {
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * "6 Oct" — the UTC calendar day of an instant. UTC because the window is
 * computed in UTC (the cron is 09:00 UTC); read in local time, the label could
 * change with the reader, and a report that does that is not a report. Built by
 * hand rather than with toLocaleDateString so the output is ASCII by
 * construction and identical on every runtime.
 */
export function formatDay(seconds: number): string {
  const d = new Date(seconds * 1000)
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`
}

/**
 * Labels the week by the day the text arrives. The window is the seven days
 * up to Monday 09:00 UTC (exclusive), so "week to 13 Oct" on a text received on
 * 13 Oct is literally true, and it is the day he reads it on.
 */
function weekLabel(windowEndSeconds: number): string {
  return `week to ${formatDay(windowEndSeconds)}`
}

/** The leads line. "none", never "0", and never a bare count for days that
 *  were not being counted. */
function leadsLine(d: DigestInput): string {
  const n = count(d.leads)
  const value = n === 0 ? 'none' : String(n)
  const partial = d.leadsCountedFrom === null ? '' : ` (counted from ${formatDay(d.leadsCountedFrom)})`
  return `New leads: ${value}${partial}`
}

/** The questions line. 0 asked is "none asked" — never "0%". */
function questionsLine(d: DigestInput): string {
  const asked = count(d.asked)
  if (asked === 0) return 'Questions: none asked'
  // Answered can never be reported above asked: the rate is capped by
  // formatRate and the number is capped here, so the line cannot contradict
  // itself even if the window cuts a question from its answer.
  const answered = Math.min(count(d.answered), asked)
  return `Questions: ${asked} asked, ${answered} answered (${formatRate(answered / asked)})`
}

/** The gaps line — questions the assistant passed to HIM. Owner-facing words,
 *  not "gaps": he does not know the engine's vocabulary and should not need to. */
function gapsLine(d: DigestInput): string {
  const opened = count(d.gapsOpened)
  const answered = count(d.gapsAnswered)
  const dismissed = count(d.gapsDismissed)
  if (opened === 0 && answered === 0 && dismissed === 0) {
    return 'Nothing was passed to you to answer.'
  }
  const parts = [`${opened} new`, `${answered} answered`]
  if (dismissed > 0) parts.push(`${dismissed} dismissed`)
  return `Passed to you: ${parts.join(', ')}`
}

/**
 * The confirm (decay) line — the ONE line allowed to go quiet.
 *
 * Present only while the chase is live AND something is due. When the chase has
 * gone quiet, the line is ABSENT, not replaced by "nothing needs confirming":
 * that would be a false report over real work. And when nothing is due the line
 * is absent too, so an absent line only ever means "no chase this week" — it
 * never claims anything either way. The dashboard holds the queue regardless.
 */
function decayLine(d: DigestInput): string | null {
  const due = count(d.decayDue)
  if (!d.decayChase || due === 0) return null
  // Kelvo's wording is shorter on purpose: an ordinary staff week must fit
  // two segments (tests/digest-kelvo.test.ts), and it carries a line more.
  if (d.staff) return `${describeQueue(due)} - staff still hear them.`
  return `${describeQueue(due)} - customers are still being told these.`
}

/* ---------------------------------------------------------------------------
 * A staff pod's lines (Kelvo, Build 10e).
 * ------------------------------------------------------------------------- */

/** "Staff asked 14: 12 answered (85%)" — answered from what is written down.
 *  Short because an ordinary staff week must fit two segments. 0 asked is the
 *  quiet line instead, never "0%". */
function staffQuestionsLine(d: DigestInput): string {
  const asked = count(d.asked)
  const answered = Math.min(count(d.answered), asked)
  return `Staff asked ${asked}: ${answered} answered (${formatRate(answered / asked)})`
}

/** What staff asked that was not written down. The safety count rides on the
 *  new ones only: those are the ones opened this week, which is what it
 *  counts. "none on safety" is said when the split exists and is zero — that
 *  is news a builder wants — and nothing is said when there is no split. */
function notWrittenDownLine(d: DigestInput, staff: StaffWeek): string {
  const opened = count(d.gapsOpened)
  const answered = count(d.gapsAnswered)
  const dismissed = count(d.gapsDismissed)
  if (opened === 0 && answered === 0 && dismissed === 0) {
    return 'Nothing new came up that is not written down.'
  }
  const safety =
    staff.safetyGaps === null || opened === 0
      ? ''
      : count(staff.safetyGaps) === 0
        ? ' (none on safety)'
        : ` (${Math.min(count(staff.safetyGaps), opened)} on safety)`
  const parts = [`${opened} new${safety}`, `${answered} answered`]
  if (dismissed > 0) parts.push(`${dismissed} dismissed`)
  return `Not written down: ${parts.join(', ')}`
}

/** The renewals line: what has lapsed and what is coming, by the Renewals
 *  page's own bands. Always present on a pod with a list — "none" is the
 *  answer an owner is glad to read, and a line that vanished would read as
 *  "no longer checked". */
function renewalsLine(r: { overdue: number; soon: number }): string {
  const overdue = count(r.overdue)
  const soon = count(r.soon)
  if (overdue === 0 && soon === 0) return 'Renewals: none overdue or due in 30 days'
  const parts: string[] = []
  if (overdue > 0) parts.push(`${overdue} overdue`)
  if (soon > 0) parts.push(`${soon} due in 30 days`)
  return `Renewals: ${parts.join(', ')}`
}

/**
 * Compose the Monday text.
 *
 * ALWAYS RETURNS A MESSAGE. There is no input for which this says nothing: a
 * report that stops reporting is the product sulking, and a dead week is
 * exactly the week the owner most needs to know it is still switched on.
 *
 * A dead week — no leads and nobody asked anything — collapses the two activity
 * lines into one plain sentence, rather than "none" twice, because that is the
 * truth stated once. The other lines are unaffected: he may still have answered
 * questions from earlier weeks, and that work is his and is reported.
 */
export function formatWeeklyDigest(d: DigestInput): string {
  const brand = capped(d.brandName, MAX_BRAND_CHARS)
  const product = capped(d.productName, MAX_PRODUCT_CHARS)

  const lines: string[] = [`${brand}: your ${product} ${weekLabel(d.windowEndSeconds)}`]

  if (d.staff) {
    lines.push(count(d.asked) === 0 ? 'A quiet week: no questions from staff.' : staffQuestionsLine(d))
    lines.push(notWrittenDownLine(d, d.staff))
    if (d.staff.renewals) lines.push(renewalsLine(d.staff.renewals))
    const decayStaff = decayLine(d)
    if (decayStaff) lines.push(decayStaff)
    lines.push(`See it all: ${toGsm7(d.dashboardUrl)}`)
    lines.push(`Reply ${DIGEST_OFF_KEYWORD} to stop these.`)
    return lines.join('\n')
  }

  const quiet = count(d.leads) === 0 && count(d.asked) === 0
  if (quiet) {
    const partial =
      d.leadsCountedFrom === null ? '' : ` (leads counted from ${formatDay(d.leadsCountedFrom)})`
    lines.push(`A quiet week: no enquiries.${partial}`)
  } else {
    lines.push(leadsLine(d))
    lines.push(questionsLine(d))
  }

  lines.push(gapsLine(d))

  const decay = decayLine(d)
  if (decay) lines.push(decay)

  lines.push(`See it all: ${toGsm7(d.dashboardUrl)}`)
  lines.push(`Reply ${DIGEST_OFF_KEYWORD} to stop these.`)

  return lines.join('\n')
}

/* ---------------------------------------------------------------------------
 * The partial-week rule, as a pure function so it is tested here and the
 * engine (commit C) only supplies the instant.
 * ------------------------------------------------------------------------- */

/**
 * The instant from which lead_captured is known to be emitted in production —
 * on BOTH deployments, so the later of the two.
 *
 * WHICH DEPLOY COUNTS. `main` deploys Kelvo; the `foredesk` branch deploys
 * Foredesk (.github/workflows/deploy.yml). Commit A reached Kelvo on 6 Oct 2026
 * at about 10:26 UTC, but Foredesk only when main is promoted to the foredesk
 * branch. This value was first set from the Kelvo deploy, which was wrong for
 * Foredesk, and was corrected before promotion. It is set to 22:00 UTC on
 * 6 Oct (23:00 BST): the promotion is made before then, so the value is AFTER
 * the true start on both Workers.
 *
 * THE DIRECTION OF THE ERROR IS THE WHOLE RULE. A window starting before this
 * instant is labelled partial. Set it too EARLY and a window that began before
 * the real start would read as whole and report "New leads: 0" over hours in
 * which leads were captured but not counted — a false zero. Set it late and the
 * worst case is a "counted from 6 Oct" note covering a few hours that were in
 * fact counted, which is still what the note says. So it errs late, never early.
 */
export const LEAD_EVENTS_SINCE = Date.UTC(2026, 9, 6, 22, 0) / 1000

/** Null when the whole window was counted; otherwise the instant counting began. */
export function leadsCountedFrom(windowStartSeconds: number): number | null {
  return windowStartSeconds < LEAD_EVENTS_SINCE ? LEAD_EVENTS_SINCE : null
}

/* ---------------------------------------------------------------------------
 * The owner's switch on the dashboard (Build 9, commits F and G).
 *
 * The shape the engine sends and the words the card shows live HERE, in the
 * dependency-free module both lanes import — the same split as the counters —
 * so every state the owner can be in is worded and tested in one place, not in
 * JSX where nothing can assert it.
 * ------------------------------------------------------------------------- */

/** What GET /api/admin/digest returns. */
export interface DigestSetting {
  /** The operator has switched the digest on for this pod (pod.weeklyDigest). */
  available: boolean
  /** The pod has a UK mobile to text it to. */
  hasMobile: boolean
  /** Last three digits of that mobile, so he can see where it goes. Null if none. */
  phoneEnding: string | null
  /** He has not turned it off (no DIGEST OFF, or a later DIGEST ON). */
  on: boolean
}

/**
 * Never trust the wire. Anything missing or garbled reads as the state that
 * promises LEAST: not available, no mobile, off — so a broken response can
 * never draw "On — we text you every Monday" over a digest that is not coming.
 */
export function coerceDigestSetting(raw: unknown): DigestSetting {
  const r = (raw ?? {}) as Record<string, unknown>
  const ending = typeof r.phoneEnding === 'string' && /^\d{3}$/.test(r.phoneEnding) ? r.phoneEnding : null
  return {
    available: r.available === true,
    hasMobile: r.hasMobile === true,
    phoneEnding: ending,
    on: r.on === true,
  }
}

/** The card, as words. `action` is the one button it offers, or none. */
export interface DigestCard {
  status: 'On' | 'Off' | 'Not switched on'
  body: string
  note: string
  action: { turnOn: boolean; label: string } | null
}

/**
 * Four states, four distinct readings — and only two of them offer a button:
 *   - not switched on for this account: the OPERATOR's decision, not his, so
 *     no button pretends he can change it;
 *   - no mobile to text: no button, because "On" would promise a text that has
 *     nowhere to go;
 *   - on: says exactly where it goes and when, and offers Turn off;
 *   - off: says HE turned it off, and offers Turn on.
 *
 * "Monday morning", never "10am": the cron is 09:00 UTC, which is 10:00 in
 * summer and 09:00 in winter. A time that is wrong for half the year is a
 * small lie told every Monday.
 */
export function describeDigestSetting(s: DigestSetting): DigestCard {
  if (!s.available) {
    return {
      status: 'Not switched on',
      body: 'The weekly report is not switched on for your account yet.',
      note: '',
      action: null,
    }
  }
  if (!s.hasMobile) {
    return {
      status: 'Not switched on',
      body: 'There is no mobile number on your account to text it to.',
      note: '',
      action: null,
    }
  }
  const where = s.phoneEnding ? `your mobile ending ${s.phoneEnding}` : 'your mobile'
  if (s.on) {
    return {
      status: 'On',
      body: `Every Monday morning we text ${where} what your assistant did that week.`,
      note: `You can also reply ${DIGEST_OFF_KEYWORD} to the text.`,
      action: { turnOn: false, label: 'Turn off' },
    }
  }
  return {
    status: 'Off',
    body: 'You turned the weekly report off. Nothing is texted on Mondays until you turn it back on.',
    note: `You can also reply ${DIGEST_ON_KEYWORD} to any earlier report.`,
    action: { turnOn: true, label: 'Turn on' },
  }
}
